import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'

import makeStyles from '@mui/styles/makeStyles'
import { Box, Button, ButtonGroup, Typography } from '@mui/material'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'
import SVG from 'react-inlinesvg'
import { apiJSON } from '@shared/utils/api/api.util'
import { useRouter } from '@shared/hooks/use-router'
import { ProgressButton } from '@shared/components/progress-button.component'
import { useScopesQuery } from '@shared/hooks/use-scopes-query.hook'
import {
  Audit,
  BaseRequirementGroupedByScopes,
  Roles,
  Scope,
} from '@shared/types'
import { BackdropProgress } from '@shared/components/backdrop-progress.component'
import TreeCheckbox from './requirements-tree.component'
import { useAuditQuery } from '@shared/hooks/use-audit-query.hook'
import { useACL } from '@shared/hooks/use-acl.hook'

enum DroppableScope {
  freeScopes = 'freeScopes',
  auditScopes = 'auditScopes',
  esqulaScopes = 'esqulaScopes',
}

const buildScopesList = (ids: number[], scopes: Scope[]): Scope[] => {
  return ids
    .map((id) => scopes.find((scope) => scope.scopeID === id))
    .filter(Boolean) as Scope[]
}

export function SelectScopesPage() {
  const { query, navigate, location } = useRouter<{
    companyID: string
    auditID?: string
  }>()
  const { companyID, auditID } = query
  const isEditMode = Boolean(auditID)

  const { is: isAllowed } = useACL()
  const isAuditor = isAllowed([Roles.Auditor])
  const isAdmin = isAllowed([Roles.Admin])
  const isConfig = location.pathname.includes('config')
  const classes = useStyles()
  const [defaultFreeScopes, setDefaultFreeScopes] = useState<Scope[]>([])
  const [freeScopes, setFreeScopes] = useState<Scope[]>([])
  const [auditScopes, setAuditScopes] = useState<Scope[]>([])
  const [esqulaScopes, setEsqulaScopes] = useState<Scope[]>([])
  const [fetchedBaseRequirements, setFetchedBaseRequirements] = useState<
    BaseRequirementGroupedByScopes[]
  >([])
  const [selectedEsqulaRequirements, setSelectedEsqulaRequirements] = useState<
    number[]
  >([])
  const [deselectedScopes, setDeselectedScopes] = React.useState<
    { scopeId: number; excludedRequirements: number[] }[]
  >([])

  const { areScopesLoading, scopes: fetchedScopes } = useScopesQuery()
  const { audit, isAuditLoading } = useAuditQuery(auditID || '', {
    enabled: isEditMode,
  })

  const { isLoading: isCompanyProfileLoading, data: companyProfile } = useQuery(
    `CompaniesEvaluationProfiles/${companyID}`,
    () => apiJSON(`CompaniesEvaluationProfiles/${companyID}`)
  )

  const { mutateAsync: createAudit, isLoading: isAuditCreating } =
    useMutation<Audit>(() =>
      apiJSON(
        `CompaniesEvaluationProfiles/${companyID}/configuration`,
        createScopesList(),
        {
          method: 'POST',
        }
      )
    )

  const { mutateAsync: updateAudit, isLoading: isUpdateAuditLoading } =
    useMutation<Audit>(() =>
      apiJSON(
        `CompaniesEvaluationProfiles/${companyID}/configuration`,
        createScopesList(),
        {
          method: 'PUT',
        }
      )
    )

  const onCancel = useCallback(() => {
    isEditMode
      ? navigate(`/audit/${companyID}/${auditID}`, { replace: true })
      : navigate(-1)
  }, [navigate, isEditMode, companyID, auditID])

  const onScopesSubmit = useCallback(async () => {
    if (companyProfile?.notConfigured) {
      try {
        await createAudit()

        navigate(`/company-profile/${companyID}`, { replace: true })
      } catch (error) {
        console.error('Error in createAudit:', error)
      }
      return
    }

    if (!companyProfile?.notConfigured) {
      try {
        const audit = await updateAudit()
        if (audit) {
          navigate(`/company-profile/${companyID}/${auditID}`, {
            replace: true,
          })
        }
        navigate(`/company-profile/${companyID}`, { replace: true })
      } catch (error) {
        console.error('Error in updateAudit:', error)
      }
    }
  }, [auditID, companyID, companyProfile, createAudit, updateAudit, navigate])

  const createScopesList = () => {
    const esqulaSelectedScopes = esqulaScopes.map((scope) => ({
      scopeId: scope.scopeID,
      excludedRequirements: [],
    }))

    const combinedScopes = [
      ...deselectedScopes,
      ...esqulaSelectedScopes.filter(
        (selected) =>
          !deselectedScopes.some(
            (deselected) => deselected.scopeId === selected.scopeId
          )
      ),
    ].filter(
      ({ scopeId }) =>
        !freeScopes.map(({ scopeID }) => scopeID).includes(scopeId)
    )

    return {
      scopesInAudit: auditScopes.map((scope) => scope.scopeID),
      scopesInEsqula: combinedScopes,
    }
  }

  const checkIfEsqulaScopesHasNoSelected = (
    selectedRequirements: number[],
    deselectedScopes: { scopeId: number; excludedRequirements: number[] }[],
    esqulaScopes: { scopeID: number }[],
    fetchedBaseRequirements: BaseRequirementGroupedByScopes[]
  ) => {
    const validDeselectedScopes = deselectedScopes.filter((d) =>
      esqulaScopes.some((e) => e.scopeID === d.scopeId)
    )

    const transformedResult = validDeselectedScopes.map((deselectedScope) => {
      const scope = fetchedBaseRequirements.find(
        (s) => s.id === deselectedScope.scopeId
      )

      if (!scope) return null

      const allRequirements = scope.groupedRequirementBases.flatMap(
        (group: { baseRequirements: { id: number }[] }) =>
          group.baseRequirements.map((req) => req.id)
      )

      const remainingRequirements = allRequirements.filter(
        (reqId: number) => !deselectedScope.excludedRequirements.includes(reqId)
      )

      const hasSelected = remainingRequirements.some((reqId: number) =>
        selectedRequirements.includes(reqId)
      )

      return hasSelected
    })

    return transformedResult.every((result) => result === true)
  }

  const isValidSelection = useMemo(() => {
    return checkIfEsqulaScopesHasNoSelected(
      selectedEsqulaRequirements,
      deselectedScopes,
      esqulaScopes,
      fetchedBaseRequirements
    )
  }, [
    selectedEsqulaRequirements,
    deselectedScopes,
    esqulaScopes,
    fetchedBaseRequirements,
  ])

  const getListByDroppableId = (droppableId: DroppableScope) => {
    switch (droppableId) {
      case 'freeScopes':
        return [...freeScopes]
      case 'auditScopes':
        return [...auditScopes]
      case 'esqulaScopes':
        return [...esqulaScopes]
      default:
        return []
    }
  }

  const setListByDroppableId = (
    droppableId: DroppableScope,
    updatedList: Scope[]
  ) => {
    switch (droppableId) {
      case 'freeScopes':
        setFreeScopes(updatedList)
        break
      case 'auditScopes':
        setAuditScopes(updatedList)
        break
      case 'esqulaScopes':
        setEsqulaScopes(updatedList)
        break
    }
  }

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result

    if (!destination) return

    const sourceDroppableId = source.droppableId as DroppableScope
    const destinationDroppableId = destination.droppableId as DroppableScope

    if (!isConfig) {
      if (
        sourceDroppableId === DroppableScope.auditScopes &&
        destinationDroppableId === DroppableScope.esqulaScopes
      ) {
        return
      }

      if (
        sourceDroppableId === DroppableScope.esqulaScopes &&
        destinationDroppableId === DroppableScope.auditScopes
      ) {
        return
      }

      if (
        sourceDroppableId === DroppableScope.freeScopes &&
        defaultFreeScopes.some(
          (item) => item.scopeID === +result.draggableId
        ) &&
        destinationDroppableId === DroppableScope.auditScopes
      ) {
        return
      }
    }

    if (sourceDroppableId === destinationDroppableId) {
      return
    }

    const sourceList = getListByDroppableId(sourceDroppableId)
    const destinationList = getListByDroppableId(destinationDroppableId)
    const [movedItem] = sourceList.splice(source.index, 1)

    destinationList.splice(destination.index, 0, movedItem)

    setListByDroppableId(sourceDroppableId, sourceList)
    setListByDroppableId(destinationDroppableId, destinationList)
  }

  useEffect(() => {
    if (fetchedScopes?.length) {
      setFreeScopes(fetchedScopes)

      if (
        companyProfile?.scopesUnderAssessment ||
        companyProfile?.scopesInEsqula
      ) {
        const underAssessmentAuditScopesIds =
          companyProfile?.scopesUnderAssessment.map((scope: number) => scope) ||
          []

        const assessedAuditScopesIds =
          companyProfile?.assessedScopes.map(
            (scope: { id: number }) => scope.id
          ) || []

        const esqulaScopesIds =
          companyProfile?.scopesInEsqula.map(
            (scope: { id: number }) => scope.id
          ) || []

        const filteredAssessedAuditScopesIds = assessedAuditScopesIds.filter(
          (id: number) => !underAssessmentAuditScopesIds.includes(id)
        )

        const freeScopesIds = fetchedScopes
          .map((scope) => scope.scopeID)
          .filter(
            (scopeId) =>
              ![
                ...underAssessmentAuditScopesIds,
                ...esqulaScopesIds,
                ...assessedAuditScopesIds,
              ].includes(scopeId)
          )

        setDefaultFreeScopes(buildScopesList(freeScopesIds, fetchedScopes))
        setFreeScopes(buildScopesList(freeScopesIds, fetchedScopes))
        setAuditScopes(
          buildScopesList(
            [
              ...underAssessmentAuditScopesIds,
              ...filteredAssessedAuditScopesIds,
            ],
            fetchedScopes
          )
        )
        setEsqulaScopes(buildScopesList(esqulaScopesIds, fetchedScopes))
      }
    }
  }, [companyProfile, fetchedScopes])

  useEffect(() => {
    if (audit) {
      setSelectedEsqulaRequirements(
        audit.auditRequirements.map(
          ({ requirementBaseID }) => requirementBaseID
        )
      )
    }
  }, [audit])

  return !areScopesLoading &&
    !isAuditLoading &&
    !isCompanyProfileLoading &&
    fetchedScopes?.length ? (
    <>
      <h2 className={classes.header}>
        Zarządzasz zakresami widocznymi dla klienta
      </h2>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={classes.columnContainer}>
          {[
            {
              droppableId: DroppableScope.freeScopes,
              scopes: freeScopes,
              label: 'Nieużywane',
            },
            {
              droppableId: DroppableScope.auditScopes,
              scopes: auditScopes,
              label: 'Audyt',
            },
            {
              droppableId: DroppableScope.esqulaScopes,
              scopes: esqulaScopes,
              label: 'Esqula',
            },
          ].map(({ droppableId, scopes, label }) => (
            <div
              key={droppableId}
              className={
                isAuditor &&
                droppableId === DroppableScope.auditScopes &&
                !isConfig
                  ? classes.disabledColumn
                  : classes.column
              }
            >
              <h3>{label}</h3>
              <Droppable
                droppableId={droppableId}
                isDropDisabled={
                  (isAuditor &&
                    droppableId === DroppableScope.auditScopes &&
                    !isConfig) ??
                  false
                }
              >
                {(droppable) => (
                  <div
                    className={`${classes.innerColumn} ${isAuditor && droppableId === DroppableScope.auditScopes && !isConfig ? classes.disabledColumn : ''}`}
                    ref={droppable.innerRef}
                    {...droppable.droppableProps}
                  >
                    {scopes.map((scope, index) => (
                      <Draggable
                        key={scope.scopeID}
                        draggableId={scope.scopeID.toString()}
                        index={index}
                        isDragDisabled={
                          (isAuditor &&
                            droppableId === DroppableScope.auditScopes &&
                            !isConfig) ||
                          (isAdmin &&
                            !isConfig &&
                            (companyProfile?.scopesUnderAssessment?.some(
                              (req: number) => req === scope.scopeID
                            ) ??
                              false))
                        }
                      >
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={
                              companyProfile?.scopesUnderAssessment?.some(
                                (req: number) => req === scope.scopeID
                              )
                                ? classes.scopeTileDisabled
                                : classes.scopeTile
                            }
                          >
                            {scope.imageURL ? (
                              <SVG
                                src={scope.imageURL}
                                className={classes.scopeIcon}
                              />
                            ) : null}
                            <p className={classes.scopeLabel}>{scope.name}</p>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppable.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
      {isAdmin && companyProfile?.scopesUnderAssessment.length > 0 && (
        <Box sx={{ marginTop: 2 }}>
          <Typography className={classes.currentScopeInfo}>
            Aktualnie trwa audyt dla tego zakresu - możesz do niego wejść.
          </Typography>
          <Box className={classes.currentScopesList}>
            <Typography className={classes.currentScopesListItem}>
              {companyProfile?.scopesUnderAssessment
                ?.map((scopeID: number) => {
                  const matchingScope = fetchedScopes.find(
                    (fetchedScope) => fetchedScope.scopeID === scopeID
                  )
                  return matchingScope ? matchingScope.name : ''
                })
                .filter((name: string) => name)
                .join(', ')}
            </Typography>
          </Box>
        </Box>
      )}
      {esqulaScopes.length ? (
        <TreeCheckbox
          type="localization"
          scopes={esqulaScopes}
          selected={selectedEsqulaRequirements}
          setSelected={setSelectedEsqulaRequirements}
          setDeselectedScopes={setDeselectedScopes}
          companyProfile={companyProfile}
          setFetchedBaseRequirements={setFetchedBaseRequirements}
          header={
            <h2 className={classes.header}>
              Wybierz wymagania do poszczególnych zakresów usługi Esqula
            </h2>
          }
        />
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {!isValidSelection && (
          <Typography marginTop="30px" marginLeft="10px">
            Wybierz przynajmniej jedno wymaganie z zakresu, aby kontynuować
          </Typography>
        )}
        <ButtonGroup className={classes.actionButtonsContainer}>
          <Button onClick={onCancel} variant="outlined">
            Anuluj
          </Button>
          <ProgressButton
            isLoading={isAuditCreating || isUpdateAuditLoading}
            onClick={onScopesSubmit}
            disabled={
              !isValidSelection ||
              isAuditCreating ||
              (!auditScopes.length && !esqulaScopes.length) ||
              isUpdateAuditLoading
            }
            variant="contained"
            color="primary"
            className={classes.actionButtonSubmit}
          >
            Dalej
          </ProgressButton>
        </ButtonGroup>
      </Box>
    </>
  ) : (
    <BackdropProgress />
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    columnContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    column: {
      margin: '0 10px',
      backgroundColor: theme.palette.grey[100],
      padding: '10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      borderRadius: '8px', // Optional: makes it look cleaner
    },
    disabledColumn: {
      margin: '0 10px',
      backgroundColor: theme.palette.grey[100],
      padding: '10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      borderRadius: '8px',
      opacity: 0.3,
    },
    innerColumn: {
      flex: 1,
      width: '250px',
    },
    scopesContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    scopeTile: {
      display: 'flex',
      color: theme.palette.grey[700],
      backgroundColor: theme.palette.grey[100],
      border: `1px solid ${theme.palette.grey[300]}`,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px',
      userSelect: 'none',
      marginBottom: '5px',
    },
    scopeTileDisabled: {
      display: 'flex',
      color: theme.palette.grey[700],
      backgroundColor: theme.palette.grey[100],
      border: `1px solid ${theme.palette.grey[300]}`,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px',
      userSelect: 'none',
      marginBottom: '5px',
      opacity: 0.3,
    },
    scopeIcon: {
      marginRight: theme.spacing(1),
      width: 40,
      height: 40,
      fill: theme.palette.primary.main,
    },
    scopeLabel: {
      flex: 5,
      marginTop: 0,
      marginBottom: 0,
      fontWeight: '500',
      textAlign: 'center',
    },
    header: {
      color: theme.palette.grey[600],
      textAlign: 'center',
      fontWeight: 300,
    },
    actionButtonsContainer: {
      marginTop: '30px',
      marginLeft: 'auto',
      marginRight: '10px',
    },
    actionButtonSubmit: {
      marginLeft: 20,
    },
    currentScopeInfo: {
      textAlign: 'center',
    },
    currentScopesList: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px',
      maxWidth: '700px',
      margin: 'auto',
    },
    currentScopesListItem: {
      margin: '0px',
      fontWeight: 'bold',
    },
  }
})
